import React from 'react';
import { Loader2, FileText, Stethoscope, UserPlus, Hash, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

/**
 * ActionButtons - Generate and Clear buttons section
 * Extracted from App.js for maintainability
 */
export const ActionButtons = ({
  effectiveRole,
  isGenerating,
  notes,
  generateDocuments,
  clearAll,
  // Mar 2, 2026 — Pre-Encounter visibility chip. Shows which context blocks
  // (Last Visit / Pre-Visit Prep) are loaded into the current payload so the
  // provider can see at a glance what will be sent to the LLM before hitting
  // Generate. Hovering the chip shows a preview of the content.
  priorVisitContext,
  prepNotes,
}) => {
  const hasPrior = priorVisitContext && priorVisitContext.trim().length > 0;
  const hasPrep = prepNotes && prepNotes.trim().length > 0;
  const showChip = effectiveRole === 'provider' && (hasPrior || hasPrep);
  const labels = [];
  if (hasPrior) labels.push('Last Visit');
  if (hasPrep) labels.push('Pre-Visit Prep');
  const preview = [
    hasPrior ? `Last Visit: ${priorVisitContext.trim().slice(0, 140)}${priorVisitContext.trim().length > 140 ? '…' : ''}` : null,
    hasPrep ? `Prep Notes: ${prepNotes.trim().slice(0, 140)}${prepNotes.trim().length > 140 ? '…' : ''}` : null,
  ].filter(Boolean).join('\n\n');

  return (
    <div className="space-y-2 pt-2">
      {showChip && (
        <div
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-100/90"
          title={preview}
          data-testid="pre-encounter-loaded-chip"
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
          <span className="font-semibold">Pre-Encounter included:</span>
          <span className="text-amber-100/70">{labels.join(' · ')}</span>
          <span className="ml-auto text-[10px] text-amber-100/50">hover to preview</span>
        </div>
      )}
      <div className="flex gap-3">
        <Button 
          onClick={generateDocuments} 
          disabled={isGenerating || !notes.trim()} 
          className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground h-11 text-sm font-medium transition-all active:scale-[0.98]" 
          data-testid="generate-button"
          title={isGenerating ? "Generating..." : (notes?.trim() ? "Generate" : "Type or dictate notes first")}
        >
          {isGenerating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
          ) : effectiveRole === 'patient' ? (
            <><FileText className="w-4 h-4 mr-2" />Generate Letter</>
          ) : (
            <><img src="/emblem_icon.png" alt="" className="w-6 h-6 mr-2 object-contain inline-block" />Generate</>
          )}
        </Button>
        <Button 
          onClick={() => clearAll()} 
          variant="outline" 
          className="h-11 px-5 border-border text-muted-foreground hover:bg-secondary" 
          data-testid="clear-button"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {effectiveRole === 'patient' ? 'New Appt' : 'Next Patient'}
        </Button>
      </div>
    </div>
  );
};

/**
 * ProviderAdvancedOptions - Advanced options collapsible for Provider mode
 * Extracted from App.js for maintainability
 */
export const ProviderAdvancedOptions = ({
  showAdvanced,
  setShowAdvanced,
  charLimit,
  setCharLimit
}) => {
  return (
    <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-8">
          Advanced Options
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-xs flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" />Char Limit
            </Label>
            <Input 
              type="number" 
              placeholder="500" 
              value={charLimit} 
              onChange={(e) => setCharLimit(e.target.value)} 
              className="h-9 text-sm font-mono bg-input border-border" 
              data-testid="char-limit-input" 
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ActionButtons;
